import { Component, type ReactNode } from 'react';
import { fehlerMelden } from '../utils/fehlerMelden';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../utils/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // A stale chunk after a deploy surfaces here as a render error (React.lazy .default).
    // Self-heal with a one-time reload instead of showing a dead "retry" button.
    if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // Bis hierher endete jeder Renderfehler ausschliesslich in der Browserkonsole des
    // Nutzers — also genau dort, wo ihn niemand sieht, der ihn beheben koennte.
    fehlerMelden(error?.message || 'Unbehandelter Renderfehler', {
      stack: error?.stack,
      komponente: info?.componentStack?.split(/\r?\n/).find((z) => z.trim())?.trim(),
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center min-h-[300px] p-8 bg-gray-50 dark:bg-[#0a0a0f] transition-colors duration-300">
          <div className="max-w-md w-full text-center space-y-5 animate-fade-in-up">
            {/* Animated error icon with glow */}
            <div className="relative mx-auto w-20 h-20">
              <div className="absolute inset-0 rounded-full bg-red-100 dark:bg-red-500/10 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800/40 flex items-center justify-center shadow-lg dark:shadow-red-900/20">
                <AlertTriangle className="w-9 h-9 text-red-500 dark:text-red-400" />
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Etwas ist schiefgelaufen
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/50 rounded-xl p-3 text-left">
                <p className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all leading-relaxed">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-black dark:text-black shadow-lg transition-all duration-200 active:scale-[0.97] hover:shadow-xl bg-primary-500 hover:bg-primary-400"
              style={{
                background: `linear-gradient(135deg, var(--color-primary, #EAB308), color-mix(in srgb, var(--color-primary, #EAB308) 80%, black))`,
                boxShadow: `0 4px 20px -4px color-mix(in srgb, var(--color-primary, #EAB308) 30%, transparent)`,
              }}
            >
              <RefreshCw className="w-4 h-4" />
              Erneut versuchen
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
